import React, { useState } from "react";
import { X, Upload, FileSpreadsheet, Clipboard, Download } from "lucide-react";
import { toast } from "sonner";
import { tcError } from "@/lib/tc";
import { api, API, getToken } from "@/lib/api";

export default function BulkImportModal({ onClose, onDone }) {
  const [mode, setMode] = useState("file"); // 'file' | 'paste'
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/personnel/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "personel_sablonu.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi");
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const [preview, setPreview] = useState(null);
  const [forced, setForced] = useState(new Set());

  const send = async (extra) => {
    if (mode === "file") {
      const fd = new FormData();
      fd.append("file", file);
      if (extra.force_rows) fd.append("force_rows", extra.force_rows.join(","));
      return api.post(`/personnel/import${extra.dry_run ? "?dry_run=true" : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
    return api.post("/personnel/import-text", { text, ...extra });
  };

  const runPreview = async () => {
    if (mode === "file" && !file) { toast.error("Lütfen bir .xlsx dosyası seçin"); return; }
    if (mode === "paste" && !text.trim()) { toast.error("Yapıştırma alanı boş"); return; }
    setBusy(true);
    try {
      const r = await send({ dry_run: true });
      setPreview(r.data);
      setForced(new Set());
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Önizleme alınamadı");
    } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const r = await send({ force_rows: [...forced] });
      onDone(r.data);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "İçe aktarım başarısız");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-2 sm:p-4" data-testid="bulk-import-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <Upload size={18} className="text-indigo-700" /> Toplu Personel Ekleme
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Önce önizleme gösterilir · mükerrer satırlar işaretlenir
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              data-testid="mode-file"
              onClick={() => setMode("file")}
              className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${mode === "file" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}
            >
              <FileSpreadsheet size={13} /> Dosya Yükle
            </button>
            <button
              data-testid="mode-paste"
              onClick={() => setMode("paste")}
              className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${mode === "paste" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}
            >
              <Clipboard size={13} /> Yapıştır
            </button>
          </div>
          <button
            data-testid="download-template"
            onClick={downloadTemplate}
            className="ml-auto inline-flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-lg px-3 py-2 text-xs font-semibold"
          >
            <Download size={12} /> Şablon İndir
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {preview ? (
            <ImportPreviewTable preview={preview} forced={forced} setForced={setForced} />
          ) : mode === "file" ? (
            <div className="space-y-3">
              <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center">
                <FileSpreadsheet size={28} className="text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600 mb-3">
                  <strong>Şablonu indir</strong>, satırları doldur ve geri yükle
                </p>
                <input
                  data-testid="import-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block mx-auto text-xs"
                />
                {file && (
                  <p className="mt-2 text-xs text-emerald-700 font-semibold">
                    Seçildi: {file.name}
                  </p>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                Sütunlar: TC Kimlik No · Ad Soyad · Pozisyon · Aylık Brüt · İşe Başlama (YYYY-AA-GG) · İşten Çıkış (opsiyonel) · Aktif (E/H)
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-600">
                Excel/Sheets'ten kopyaladığınız satırları aşağıya yapıştırın. Sütunlar sekme, noktalı virgül veya virgülle ayrılmalı. İlk satır başlıksa otomatik atlanır.
              </p>
              <textarea
                data-testid="import-text-area"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                placeholder={"TC Kimlik No\tAd Soyad\tPozisyon\tBrüt\tBaşlama\tÇıkış\tAktif\n101\tAhmet Yılmaz\tAvukat\t50000\t2024-01-15\t\tE\n102\tMehmet Demir\tStajyer\t30000\t2023-10-01\t2025-03-01\tE"}
                className="w-full border border-slate-200 rounded-lg p-3 text-xs font-mono-tab leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={preview ? () => setPreview(null) : onClose}
            data-testid="import-back-btn"
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          >{preview ? "Geri" : "İptal"}</button>
          <button
            data-testid="submit-import"
            onClick={preview ? confirmImport : runPreview}
            disabled={busy || (preview && importCount(preview, forced) === 0)}
            className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Upload size={14} /> {busy ? "Yükleniyor..." : preview ? `${importCount(preview, forced)} Personeli İçe Aktar` : "Önizle"}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS = {
  ok: { label: "Yeni", cls: "bg-emerald-50 text-emerald-700" },
  duplicate: { label: "Mükerrer", cls: "bg-amber-100 text-amber-800" },
  sicil_taken: { label: "Atlanacak", cls: "bg-rose-50 text-rose-700" },
};

const importCount = (preview, forced) =>
  preview.rows.filter((r) => r.status === "ok" || (r.status === "duplicate" && forced.has(r.row))).length;

function ImportPreviewTable({ preview, forced, setForced }) {
  const dupCount = preview.rows.filter((r) => r.status === "duplicate").length;
  const toggle = (row) => setForced((prev) => {
    const n = new Set(prev);
    if (n.has(row)) n.delete(row); else n.add(row);
    return n;
  });
  const tcBad = preview.rows.filter((r) => tcError(r.sicil_no));
  return (
    <div className="space-y-3" data-testid="personnel-import-preview">
      {tcBad.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-900" data-testid="personnel-import-tc-warning">
          <strong>{tcBad.length} satırda TC Kimlik No geçersiz görünüyor</strong> (satır {tcBad.map((r) => r.row).join(", ")}).
          Kaydedilebilir, ancak kontrol etmeniz önerilir · sonradan Personel sayfasındaki uyarı panelinden düzeltilebilir.
        </div>
      )}
      {dupCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900" data-testid="personnel-import-dup-warning">
          <strong>{dupCount} satır mükerrer</strong> — aynı TC kimlik no ve ad soyad sistemde ya da dosyada zaten var.
          Varsayılan olarak atlanır; eklemek istediklerinizi "Yine de ekle" ile işaretleyin.
        </div>
      )}
      {(preview.errors || []).length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800">
          {preview.errors.join(" · ")}
        </div>
      )}
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            {["Satır", "TC Kimlik No", "Ad Soyad", "Pozisyon", "Durum", ""].map((h) => <th key={h} className="text-left px-2 py-1.5 font-bold">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {preview.rows.map((r) => (
            <tr key={r.row} className={r.status === "duplicate" ? "bg-amber-50/60" : r.status === "sicil_taken" ? "bg-rose-50/40" : ""} data-testid={`personnel-import-row-${r.row}`}>
              <td className="px-2 py-1.5 font-mono-tab text-slate-500">{r.row}</td>
              <td className="px-2 py-1.5 font-mono-tab">
                {r.sicil_no}
                {tcError(r.sicil_no) && <div className="text-[10px] font-semibold text-rose-700" data-testid={`personnel-import-tc-${r.row}`}>TC geçersiz: {tcError(r.sicil_no)}</div>}
              </td>
              <td className="px-2 py-1.5 font-semibold text-slate-900">{r.ad_soyad}</td>
              <td className="px-2 py-1.5 text-slate-600">{r.pozisyon || "-"}</td>
              <td className="px-2 py-1.5">
                <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span>
                {r.note && <div className="text-[10px] text-slate-500 mt-0.5">{r.note}</div>}
              </td>
              <td className="px-2 py-1.5 whitespace-nowrap">
                {r.status === "duplicate" && (
                  <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 cursor-pointer">
                    <input type="checkbox" checked={forced.has(r.row)} onChange={() => toggle(r.row)} data-testid={`personnel-import-force-${r.row}`} />
                    Yine de ekle
                  </label>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
